import { consoleFor } from "../../rest/rules/console";
import type { RestClientConfig } from "../../rest/rules/sdk";
import { mountayaFor } from "../../rest/rules/sdk";

/**
 * The namespaces the organization domain reaches, one seam each, so the modules below name
 * an operation rather than a path, and say, at the call site, which of the two clients
 * answers it.
 *
 * Most of them are the PUBLISHED client's. The contract publishes an organization's own
 * administration, its record, its settings, its members, its publishable keys, its usage
 * series and its billing, because managing through the API the account you already own is
 * the point of having an API. What the console keeps is narrow and stays that way: the
 * staff-only operations (the settings replace, the agreement terms, the organization search),
 * SECRET keys, which mint session tokens and are server-side credential material the contract
 * deliberately hands out no client method for, and the transfer of ownership, an owner's act a
 * session minted from such a key must never reach.
 *
 * Both clients bind the SAME transport, `consoleFor` takes its transport off the very client
 * `mountayaFor` built, so which seam a call came through changes the operation table and
 * nothing else: one credential projection, one retry policy, one date revival, one error
 * taxonomy. A read and the write that invalidates it stay coherent across the split.
 */

/**
 * billingFor is the published billing namespace for a config: an organization's subscription
 * summary, and the writes that shape it, the provisioning that opens its billing account (with
 * the promotion code for its first subscription) and the inline card-on-file pair whose attach
 * opens its Data API subscription.
 *
 * Every operation under it is organization-scoped, which is what makes it publishable: an
 * invoice, a stored card and a tax identity belong to the account, and the account is exactly
 * what an integrator holds a credential for.
 */
export const billingFor = (config: RestClientConfig) => mountayaFor(config).admin.billing;

/**
 * organizationsFor is the published organizations namespace for a config: the organization
 * record and profile, its Embedded Studio settings, the AI provider configuration the studio
 * co-pilot runs on, and the per-surface usage series.
 */
export const organizationsFor = (config: RestClientConfig) =>
  mountayaFor(config).admin.organizations;

/**
 * membersFor is the published members namespace for a config: the people who have joined an
 * organization, the change of a member's role, and a member's removal (a leave when the member is
 * the caller). The transfer of ownership is not on it: see {@link ownershipFor}.
 */
export const membersFor = (config: RestClientConfig) => mountayaFor(config).admin.members;

/**
 * ownershipFor is the CONSOLE's members namespace for a config: the transfer of an
 * organization's ownership, and nothing else.
 *
 * What only an owner does is done from this console alone. A secret key mints sessions naming any
 * member, the owner included, so an owner act a key could reach would let anybody holding one,
 * an admin who made it included, take the organization. The contract publishes no method for it,
 * and the API answers a publishable key on it with a 403 whoever its session token names.
 */
export const ownershipFor = (config: RestClientConfig) => consoleFor(config).admin.members;

/**
 * invitationsFor is the published invitations namespace for a config: the invitations to an
 * organization still waiting for an answer, one invitation made per request, and its withdrawal.
 */
export const invitationsFor = (config: RestClientConfig) => mountayaFor(config).admin.invitations;

/**
 * usersFor is the published namespace of the signed-in user for a config: their memberships,
 * the invitations waiting for them and their answers to them, and their display preferences.
 * Every operation under it acts as the user the session names, so none takes a user id.
 */
export const usersFor = (config: RestClientConfig) => mountayaFor(config).admin.users;

/**
 * accountsFor is the CONSOLE's organization namespace for a config, as far as an organization's
 * own account goes: the entitlements its agreement grants, how much of each counted limit is in
 * use, its subscription summary and whether the user may change billing. The public organization
 * carries none of it, because it is answered to a share link and a publishable key as well, and
 * the console reads it here, with a console session.
 */
export const accountsFor = (config: RestClientConfig) => consoleFor(config).admin.organizations;

/**
 * publishableKeysFor is the published keys namespace for a config: the keys an organization
 * issues to its own front ends. A publishable key is safe in a browser by construction, it
 * names an organization rather than a person, is scoped, and cannot mint anything, so
 * rotating one from a script is an ordinary thing for an integrator to do, and the contract
 * publishes all four operations.
 */
export const publishableKeysFor = (config: RestClientConfig) => mountayaFor(config).admin.keys;

/**
 * secretKeysFor is the CONSOLE's keys namespace for a config: the same four operations, for
 * the other kind of key.
 *
 * This is the one seam the split in this file exists for. A secret key signs session tokens,
 * so a client method that issued or read one would put the credential that impersonates every
 * member of an organization within reach of anything holding a weaker credential. The contract
 * publishes no such method, in any language, and the console reaches these over the shared
 * transport instead.
 */
export const secretKeysFor = (config: RestClientConfig) => consoleFor(config).admin.keys;

/**
 * staffFor is the CONSOLE's staff namespace for a config: whether the signed-in user is a
 * Mountaya staff member, the search across every customer organization, and the terms of an
 * organization's agreement, its entitlement override and its staff-controlled settings.
 *
 * None of it is on the published contract, and none of it will be: it decides who may read and
 * change somebody else's agreement, which no integrator holds a credential for. Every operation
 * under it but `getStaffStatus` refuses a caller who is not staff with a 403: the status read is
 * what lets the console offer a staff tool without first calling one and reading its refusal.
 */
export const staffFor = (config: RestClientConfig) => consoleFor(config).admin.organizations;
